import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Building } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { isAxiosError } from 'axios';

interface FormState {
  tenantName: string;
  tenantSlug: string;
  adminEmail: string;
  adminPassword: string;
  adminFirstName: string;
  adminLastName: string;
}

type FormField = keyof FormState;

interface FormErrors {
  tenantName?: string;
  tenantSlug?: string;
  adminEmail?: string;
  adminPassword?: string;
  adminFirstName?: string;
  adminLastName?: string;
  general?: string;
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function validateForm(values: FormState): FormErrors {
  const errors: FormErrors = {};

  if (!values.tenantName.trim()) {
    errors.tenantName = 'Tenant name is required.';
  }

  if (!values.tenantSlug.trim()) {
    errors.tenantSlug = 'Tenant slug is required.';
  } else if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(values.tenantSlug)) {
    errors.tenantSlug =
      'Slug may only contain lowercase letters, digits, and hyphens.';
  }

  if (!values.adminFirstName.trim()) {
    errors.adminFirstName = 'First name is required.';
  }

  if (!values.adminLastName.trim()) {
    errors.adminLastName = 'Last name is required.';
  }

  if (!values.adminEmail.trim()) {
    errors.adminEmail = 'Email address is required.';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.adminEmail)) {
    errors.adminEmail = 'Please enter a valid email address.';
  }

  if (!values.adminPassword) {
    errors.adminPassword = 'Password is required.';
  } else if (values.adminPassword.length < 8) {
    errors.adminPassword = 'Password must be at least 8 characters.';
  }

  return errors;
}

const INITIAL_FORM: FormState = {
  tenantName: '',
  tenantSlug: '',
  adminEmail: '',
  adminPassword: '',
  adminFirstName: '',
  adminLastName: '',
};

export function RegisterPage() {
  const { register, isLoading } = useAuth();

  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Partial<Record<FormField, boolean>>>(
    {},
  );
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

  function handleChange(field: FormField) {
    return (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;

      setForm((prev) => {
        const next = { ...prev, [field]: value };

        // Auto-generate slug from tenant name, unless user manually edited slug
        if (field === 'tenantName' && !slugManuallyEdited) {
          next.tenantSlug = slugify(value);
        }

        if (field === 'tenantSlug') {
          setSlugManuallyEdited(true);
        }

        // Re-validate on change once the field has been touched
        if (touched[field]) {
          const fieldErrors = validateForm(next);
          setErrors((prev) => ({
            ...prev,
            [field]: fieldErrors[field],
            general: '',
          }));

          // Also revalidate slug when tenant name changes and slug is auto-generated
          if (field === 'tenantName' && !slugManuallyEdited) {
            setErrors((prev) => {
              const updated: FormErrors = { ...prev };
              if (fieldErrors.tenantSlug !== undefined) {
                updated.tenantSlug = fieldErrors.tenantSlug;
              } else {
                delete updated.tenantSlug;
              }
              return updated;
            });
          }
        }

        return next;
      });
    };
  }

  function handleBlur(field: FormField) {
    return () => {
      setTouched((prev) => ({ ...prev, [field]: true }));
      const fieldErrors = validateForm(form);
      setErrors((prev) => ({ ...prev, [field]: fieldErrors[field] }));
    };
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();

    const validationErrors = validateForm(form);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setTouched({
        tenantName: true,
        tenantSlug: true,
        adminEmail: true,
        adminPassword: true,
        adminFirstName: true,
        adminLastName: true,
      });
      return;
    }

    setErrors({});

    try {
      await register({
        tenantName: form.tenantName.trim(),
        tenantSlug: form.tenantSlug.trim(),
        adminEmail: form.adminEmail.trim(),
        adminPassword: form.adminPassword,
        adminFirstName: form.adminFirstName.trim(),
        adminLastName: form.adminLastName.trim(),
      });
      // Navigation is handled inside useAuth.register()
    } catch (err) {
      let message = 'An unexpected error occurred. Please try again.';

      if (isAxiosError(err)) {
        const status = err.response?.status;
        const serverMsg = err.response?.data?.message;
        const serverMessage = Array.isArray(serverMsg) ? serverMsg.join('. ') : typeof serverMsg === 'string' ? serverMsg : null;

        if (serverMessage) {
          message = serverMessage;
        } else if (status === 409) {
          message = 'That email or organization name is already taken.';
        } else if (status === 400) {
          message = 'Please check your input and try again.';
        } else if (status === 429) {
          message = 'Too many attempts. Please wait and try again.';
        } else if (status != null && status >= 500) {
          message = 'Unable to connect to server. Please try again later.';
        } else if (!err.response) {
          message = 'Network error. Please check your connection.';
        }
      }

      setErrors({ general: message });
    }
  }

  return (
    <div className="min-h-screen flex bg-slate-950">
      {/* Left decorative panel (hidden on mobile) */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-between p-12 bg-gradient-to-br from-slate-900 to-slate-950 border-r border-slate-800">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary-600">
            <Building size={20} className="text-white" aria-hidden="true" />
          </div>
          <span className="text-xl font-bold text-white tracking-tight">
            BestAcco PMS
          </span>
        </div>

        <div>
          <blockquote className="text-slate-300 text-lg font-light leading-relaxed">
            "Managing short-stay properties at scale, made simple."
          </blockquote>
          <p className="mt-4 text-slate-500 text-sm">
            Reservations &middot; Channel Management &middot; Dynamic Pricing
            &middot; Operations
          </p>
        </div>

        <p className="text-slate-600 text-xs">
          &copy; {new Date().getFullYear()} BestAcco. All rights reserved.
        </p>
      </div>

      {/* Registration form panel */}
      <div className="flex flex-1 items-center justify-center p-6">
        <div className="w-full max-w-sm">
          {/* Mobile brand */}
          <div className="flex items-center gap-3 mb-8 lg:hidden">
            <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-primary-600">
              <Building size={18} className="text-white" aria-hidden="true" />
            </div>
            <span className="text-lg font-bold text-white">BestAcco PMS</span>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-gray-900">
                Create your account
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                Register your organization and admin account.
              </p>
            </div>

            {/* General error */}
            {errors.general && (
              <div
                role="alert"
                aria-live="assertive"
                className="mb-4 flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2.5 text-sm text-red-700"
              >
                {errors.general}
              </div>
            )}

            <form
              onSubmit={handleSubmit}
              noValidate
              aria-label="Registration form"
              className="space-y-4"
            >
              <Input
                label="Organization name"
                type="text"
                name="tenantName"
                autoComplete="organization"
                value={form.tenantName}
                onChange={handleChange('tenantName')}
                onBlur={handleBlur('tenantName')}
                error={touched.tenantName ? errors.tenantName : undefined}
                placeholder="Acme Hotels"
                required
                disabled={isLoading}
              />

              <Input
                label="Organization slug"
                type="text"
                name="tenantSlug"
                value={form.tenantSlug}
                onChange={handleChange('tenantSlug')}
                onBlur={handleBlur('tenantSlug')}
                error={touched.tenantSlug ? errors.tenantSlug : undefined}
                hint="URL-friendly identifier (auto-generated from name)"
                placeholder="acme-hotels"
                required
                disabled={isLoading}
              />

              <div className="grid grid-cols-2 gap-3">
                <Input
                  label="First name"
                  type="text"
                  name="adminFirstName"
                  autoComplete="given-name"
                  value={form.adminFirstName}
                  onChange={handleChange('adminFirstName')}
                  onBlur={handleBlur('adminFirstName')}
                  error={
                    touched.adminFirstName ? errors.adminFirstName : undefined
                  }
                  placeholder="John"
                  required
                  disabled={isLoading}
                />

                <Input
                  label="Last name"
                  type="text"
                  name="adminLastName"
                  autoComplete="family-name"
                  value={form.adminLastName}
                  onChange={handleChange('adminLastName')}
                  onBlur={handleBlur('adminLastName')}
                  error={
                    touched.adminLastName ? errors.adminLastName : undefined
                  }
                  placeholder="Doe"
                  required
                  disabled={isLoading}
                />
              </div>

              <Input
                label="Email address"
                type="email"
                name="adminEmail"
                autoComplete="email"
                value={form.adminEmail}
                onChange={handleChange('adminEmail')}
                onBlur={handleBlur('adminEmail')}
                error={touched.adminEmail ? errors.adminEmail : undefined}
                placeholder="admin@acmehotels.com"
                required
                disabled={isLoading}
              />

              <Input
                label="Password"
                type="password"
                name="adminPassword"
                autoComplete="new-password"
                value={form.adminPassword}
                onChange={handleChange('adminPassword')}
                onBlur={handleBlur('adminPassword')}
                error={
                  touched.adminPassword ? errors.adminPassword : undefined
                }
                placeholder="Min. 8 characters"
                required
                disabled={isLoading}
              />

              <Button
                type="submit"
                variant="primary"
                fullWidth
                size="lg"
                isLoading={isLoading}
                disabled={isLoading}
              >
                {isLoading ? 'Creating account...' : 'Create account'}
              </Button>
            </form>
          </div>

          <p className="mt-6 text-center text-sm text-slate-400">
            Already have an account?{' '}
            <Link
              to="/login"
              className="font-medium text-primary-400 hover:text-primary-300 transition-colors"
            >
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
